import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flame,
  Globe,
  Palmtree,
  Sparkles,
  Target,
  Trophy,
  Users as UsersIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { StatCard } from '@/components/app/StatCard';
import { CodeBlock } from '@/components/app/CodeBlock';
import { CopyableId } from '@/components/app/CopyableId';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { humanizeError } from '@/lib/apiClient';
import { getAvatarUrl } from '@/lib/cdn';
import { shortId } from '@/lib/utils';
import {
  usePhaseTasks,
  useUserAccessories,
  useUserCheckinHeatmap,
  useUserCheckins,
  useUserDetail,
  useUserGoalPhases,
  useUserGoals,
  useUserNotificationState,
  useUserPointsHistory,
} from '../hooks';
import type { UserGoal, UserPhase } from '../types';
import { UserActionsMenu } from '../components/UserActionsMenu';

export function UserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const detailQuery = useUserDetail(userId);

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <LoadingState label="Loading user" />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorState
          title="User failed to load"
          description={detailQuery.error ? humanizeError(detailQuery.error) : 'Not found.'}
          actionLabel="Retry"
          onAction={() => detailQuery.refetch()}
        />
      </div>
    );
  }

  const detail = detailQuery.data;
  const u = detail.user;

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        title={u.displayName ?? u.email}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{u.email}</span>
            <span aria-hidden>·</span>
            <CopyableId value={u.userId} display={shortId(u.userId, 8, 6)} label="User ID" />
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/notifications/users/${encodeURIComponent(u.userId)}`)}
            >
              Notification inspector
            </Button>
            <UserActionsMenu userDetail={detail} />
          </div>
        }
      />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="XP balance" value={detail.pointsBalance.toLocaleString()} icon={Trophy} />
        <StatCard label="Available XP" value={detail.availableBalance.toLocaleString()} hint="after reserves" />
        <StatCard label="Streak" value={u.userStreak ?? 0} icon={Flame} />
        <StatCard label="Active goals" value={detail.activeGoalCount} icon={Target} />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center gap-4">
            <Avatar className="h-28 w-28">
              <AvatarImage src={getAvatarUrl(u.avatarUrl || u.photoURL)} alt="" />
              <AvatarFallback>
                {(u.displayName ?? u.email).slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <CardTitle className="truncate text-base">{u.displayName ?? 'No display name'}</CardTitle>
              <CardDescription>
                Joined {u.createdAt?.slice(0, 10)} · last seen {timeAgo(u.lastLoginAt)}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row
              icon={<Globe className="h-3.5 w-3.5" />}
              label="Timezone"
              value={u.timeZone ?? '—'}
            />
            <Row
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Longest streak"
              value={`${u.longestUserStreak ?? 0}d`}
            />
            <Row
              icon={<Palmtree className="h-3.5 w-3.5" />}
              label="Holiday"
              value={u.isOnHoliday ? (u.holidayUntil ? `until ${u.holidayUntil.slice(0, 10)}` : 'on') : 'off'}
            />
            <Row label="Gender" value={u.gender ?? '—'} />
            <Row label="Age range" value={u.ageRange ?? '—'} />
            <Row label="Level" value={detail.currentLevel ?? '—'} />
            <Separator className="my-2" />
            <Row
              label="Onboarding"
              value={u.onboardingPassed === false ? <Badge variant="outline">Pending</Badge> : <Badge variant="success">Done</Badge>}
            />
            <Row
              label="Status"
              value={u.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="outline">Inactive</Badge>}
            />
            <Row label="Push tokens" value={u.deviceTokens?.length ?? 0} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UsersIcon className="h-4 w-4" /> Team & matching
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row
              label="Team"
              value={
                detail.activeTeamId ? (
                  <Link
                    to={`/teams/${encodeURIComponent(detail.activeTeamId)}`}
                    className="text-primary hover:underline"
                  >
                    {detail.teamName ?? detail.activeTeamId}
                  </Link>
                ) : (
                  '—'
                )
              }
            />
            <Row label="Team status" value={detail.teamStatus ?? '—'} />
            <Row label="Matching status" value={renderMatchingStatus(detail.matchingStatus)} />
            {detail.prerequisiteInfo && (
              <>
                <Separator className="my-2" />
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Prerequisite
                </div>
                <Row label="Required" value={detail.prerequisiteInfo.required ? 'Yes' : 'No'} />
                <Row label="Reason" value={detail.prerequisiteInfo.reason ?? '—'} />
                <Row label="Progress" value={detail.prerequisiteInfo.progressCount} />
                <Row label="Started" value={detail.prerequisiteInfo.startedAt ?? '—'} />
                <Row label="Completed" value={detail.prerequisiteInfo.completedAt ?? '—'} />
              </>
            )}
            {detail.queueInfo && (
              <>
                <Separator className="my-2" />
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  In matching queue
                </div>
                <Row label="Joined" value={detail.queueInfo.joinedQueueAt} />
                {detail.queueInfo.expiresAt && <Row label="Expires" value={detail.queueInfo.expiresAt} />}
              </>
            )}
          </CardContent>
        </Card>
      </section>

      <Tabs defaultValue="goals" className="space-y-3">
        <TabsList>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="checkins">Check-ins</TabsTrigger>
          <TabsTrigger value="points">Points history</TabsTrigger>
          <TabsTrigger value="accessories">Accessories</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="unlocks">Unlocks</TabsTrigger>
          <TabsTrigger value="prefs">Preferences</TabsTrigger>
        </TabsList>

        <TabsContent value="goals" className="space-y-3">
          <GoalsTab userId={userId} />
        </TabsContent>
        <TabsContent value="checkins" className="space-y-3">
          <CheckinsTab userId={userId} />
        </TabsContent>
        <TabsContent value="points" className="space-y-3">
          <PointsTab userId={userId} />
        </TabsContent>
        <TabsContent value="accessories" className="space-y-3">
          <AccessoriesTab userId={userId} />
        </TabsContent>
        <TabsContent value="notifications" className="space-y-3">
          <NotificationsTab userId={userId} />
        </TabsContent>
        <TabsContent value="unlocks" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Unlocked features</CardTitle>
              <CardDescription>
                Progression events written by UnlockDbService when level/streak thresholds are crossed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {u.unlockedFeaturesNormalized?.length ? (
                <ul className="divide-y text-sm">
                  {u.unlockedFeaturesNormalized.map((e) => (
                    <li key={e.key + e.unlockedAt} className="flex items-center justify-between py-2">
                      <code className="text-xs">{e.key}</code>
                      <span className="text-xs text-muted-foreground">
                        {e.unlockedAt.slice(0, 10)} · lvl {e.levelAtUnlock ?? '—'} · {e.source ?? 'unknown'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No unlocks yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="prefs" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Preferences & notification settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row label="Language" value={u.preferences?.language ?? '—'} />
              <Row
                label="Notifications enabled"
                value={u.preferences?.notificationsEnabled === false ? 'No' : 'Yes'}
              />
              <LabeledBlock label="notificationSettings">
                <CodeBlock value={u.notificationSettings ?? {}} />
              </LabeledBlock>
              <LabeledBlock label="ownedAccessoryIds">
                <CodeBlock value={u.ownedAccessoryIds ?? []} />
              </LabeledBlock>
              <LabeledBlock label="claimableAccessoryIds">
                <CodeBlock value={u.claimableAccessoryIds ?? []} />
              </LabeledBlock>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LabeledBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/users"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="h-4 w-4" />
      Back to users
    </Link>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <dt className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
        {icon}
        <span>{label}</span>
      </dt>
      <dd className="min-w-0 flex-1 text-right">{value}</dd>
    </div>
  );
}

function renderMatchingStatus(s: string): React.ReactNode {
  const tone: Record<string, 'default' | 'success' | 'warning' | 'outline'> = {
    has_team: 'success',
    in_queue: 'warning',
    needs_prerequisite: 'outline',
    needs_active_task: 'outline',
    ready_to_match: 'warning',
  };
  return <Badge variant={tone[s] ?? 'outline'}>{s}</Badge>;
}

function timeAgo(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function GoalsTab({ userId }: { userId: string }) {
  const q = useUserGoals(userId);
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Failed to load goals" description={humanizeError(q.error)} />;
  const goals = q.data?.goals ?? [];
  if (goals.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No goals yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {goals.map((g) => (
        <GoalJourneyRow key={g.goalId} userId={userId} goal={g} />
      ))}
    </div>
  );
}

function GoalJourneyRow({ userId, goal }: { userId: string; goal: UserGoal }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <Target className="h-4 w-4 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{goal.title}</div>
          <div className="text-xs text-muted-foreground">
            {goal.category ?? 'uncategorized'} · {goal.totalExperimentsCompleted} experiments ·
            created {goal.createdAt.slice(0, 10)}
          </div>
        </div>
        <Badge variant={goalStatusVariant(goal.overallStatus)}>{goal.overallStatus}</Badge>
        {goal.isDeleted && <Badge variant="outline">deleted</Badge>}
      </button>
      {expanded && (
        <div className="border-t">
          <PhaseList userId={userId} goalId={goal.goalId} />
        </div>
      )}
    </Card>
  );
}

function PhaseList({ userId, goalId }: { userId: string; goalId: string }) {
  const q = useUserGoalPhases(userId, goalId);
  if (q.isLoading) return <LoadingState inline />;
  if (q.isError)
    return (
      <div className="px-4 py-3 text-sm">
        <ErrorState title="Phases failed" description={humanizeError(q.error)} />
      </div>
    );
  const phases = q.data?.phases ?? [];
  if (phases.length === 0) {
    return (
      <p className="px-4 py-4 text-center text-sm text-muted-foreground">
        No phases yet.
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {phases.map((p) => (
        <PhaseRow key={p.phaseId} userId={userId} goalId={goalId} phase={p} />
      ))}
    </ul>
  );
}

function PhaseRow({
  userId,
  goalId,
  phase,
}: {
  userId: string;
  goalId: string;
  phase: UserPhase;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const pct =
    phase.totalTaskCount > 0
      ? Math.round((phase.completedTaskCount / phase.totalTaskCount) * 100)
      : 0;
  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="grid w-full grid-cols-[auto_1fr_auto] items-start gap-3 px-4 py-3 text-left text-sm hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="mt-0.5">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={phaseStatusVariant(phase.status)}>{phase.status}</Badge>
            <span className="text-xs text-muted-foreground">#{phase.phaseNumber}</span>
            {phase.difficulty && (
              <Badge variant="outline" className="font-normal">
                {phase.difficulty}
              </Badge>
            )}
            {phase.generationSource && (
              <Badge variant="muted" className="font-normal">
                {phase.generationSource}
              </Badge>
            )}
            {phase.userEdited && <Badge variant="outline">edited</Badge>}
            {phase.isDeleted && <Badge variant="outline">deleted</Badge>}
          </div>
          <div className="mt-1 line-clamp-2 font-medium">{phase.objective}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {phase.startDate.slice(0, 10)} → {phase.endDate.slice(0, 10)}
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>
              Done: <span className="font-medium text-foreground">{phase.completedTaskCount}</span>
              {phase.totalTaskCount > 0 && ` / ${phase.totalTaskCount}`}
            </span>
            <span>
              Overdue: <span className="font-medium text-foreground">{phase.overdueTaskCount}</span>
            </span>
            <span>
              Archived: <span className="font-medium text-foreground">{phase.archivedTaskCount}</span>
            </span>
            <span>
              Rescheduled:{' '}
              <span className="font-medium text-foreground">{phase.rescheduledTaskCount}</span>
            </span>
            {phase.evaluationResult?.thresholdPercentage != null && (
              <span>
                Threshold:{' '}
                <span className="font-medium text-foreground">
                  {phase.evaluationResult.thresholdPercentage}%
                </span>
              </span>
            )}
            {phase.evaluationResult?.currentStreak != null && (
              <span>
                Streak:{' '}
                <span className="font-medium text-foreground">
                  {phase.evaluationResult.currentStreak}d
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div className="font-medium text-foreground">{pct}%</div>
          <div className="mt-1 h-1 w-16 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          {phase.evaluationResult?.outcome && (
            <div className="mt-1 text-[10px] uppercase tracking-wide">
              {phase.evaluationResult.outcome}
            </div>
          )}
        </div>
      </button>
      {expanded && (
        <div className="border-t bg-muted/20 px-4 py-2">
          <TaskList userId={userId} goalId={goalId} phaseId={phase.phaseId} />
        </div>
      )}
    </li>
  );
}

function TaskList({
  userId,
  goalId,
  phaseId,
}: {
  userId: string;
  goalId: string;
  phaseId: string;
}) {
  const q = usePhaseTasks(userId, goalId, phaseId);
  if (q.isLoading) return <LoadingState inline />;
  if (q.isError)
    return <ErrorState title="Tasks failed" description={humanizeError(q.error)} />;
  const tasks = q.data?.tasks ?? [];
  if (tasks.length === 0)
    return (
      <p className="py-3 text-center text-xs text-muted-foreground">No tasks.</p>
    );
  return (
    <ul className="divide-y divide-border/60 text-sm">
      {tasks.map((t) => (
        <li
          key={t.taskId}
          className="flex items-center justify-between gap-3 py-2"
        >
          <div className="min-w-0">
            <div className="truncate">{t.title}</div>
            <div className="text-xs text-muted-foreground">
              due {t.dueDate.slice(0, 10)}
              {t.originalDueDate && t.originalDueDate !== t.dueDate
                ? ` · was ${t.originalDueDate.slice(0, 10)}`
                : ''}
              {t.completedAt ? ` · done ${t.completedAt.slice(0, 10)}` : ''}
            </div>
          </div>
          <Badge variant={taskStatusVariant(t.status)}>{t.status}</Badge>
        </li>
      ))}
    </ul>
  );
}

function phaseStatusVariant(
  s: string,
): 'default' | 'success' | 'warning' | 'outline' {
  const k = s.toLowerCase();
  if (k === 'active') return 'success';
  if (k === 'completed') return 'default';
  if (k === 'evaluating') return 'warning';
  return 'outline';
}

function taskStatusVariant(
  s: string,
): 'default' | 'success' | 'warning' | 'outline' | 'destructive' {
  const k = s.toLowerCase();
  if (k === 'done' || k === 'completed') return 'success';
  if (k === 'overdue') return 'destructive';
  if (k === 'rescheduled') return 'warning';
  if (k === 'archived') return 'outline';
  return 'default';
}

function AccessoriesTab({ userId }: { userId: string }) {
  const q = useUserAccessories(userId);
  if (q.isLoading) return <LoadingState />;
  if (q.isError)
    return (
      <ErrorState title="Accessories failed" description={humanizeError(q.error)} />
    );
  const owned = q.data?.ownedAccessoryIds ?? [];
  const claimable = q.data?.claimableAccessoryIds ?? [];
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Owned
            <Badge variant="outline" className="ml-2">
              {owned.length}
            </Badge>
          </CardTitle>
          <CardDescription>Accessories the user has earned or been granted.</CardDescription>
        </CardHeader>
        <CardContent>
          {owned.length === 0 ? (
            <p className="text-sm text-muted-foreground">None owned yet.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {owned.map((id) => (
                <li key={id}>
                  <Badge variant="outline" className="font-mono text-xs">
                    {id}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Claimable
            <Badge variant="outline" className="ml-2">
              {claimable.length}
            </Badge>
          </CardTitle>
          <CardDescription>
            Unclaimed rewards awaiting the user in-app (streak badges, milestones).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {claimable.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing pending.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {claimable.map((id) => (
                <li key={id}>
                  <Badge variant="success" className="font-mono text-xs">
                    {id}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationsTab({ userId }: { userId: string }) {
  const q = useUserNotificationState(userId);
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4" /> Notification state
          </CardTitle>
          <CardDescription>
            Live caps, cooldowns, and journey memberships. Click "Notification inspector" at the top
            for full decision history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <LoadingState />
          ) : q.isError ? (
            <ErrorState
              title="Failed to load state"
              description={humanizeError(q.error)}
            />
          ) : (
            <CodeBlock value={q.data?.notificationState ?? {}} />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-3 text-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Full decision log & journey activity for this user
          </div>
          <Link
            to={`/notifications/users/${encodeURIComponent(userId)}`}
            className="text-primary hover:underline"
          >
            Open inspector →
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function goalStatusVariant(s: string): 'default' | 'success' | 'warning' | 'outline' {
  if (s === 'active') return 'success';
  if (s === 'graduated') return 'default';
  return 'outline';
}

function CheckinsTab({ userId }: { userId: string }) {
  const checkins = useUserCheckins(userId, 30);
  const heatmap = useUserCheckinHeatmap(userId, 90);

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Last 90 days</CardTitle>
          <CardDescription>Daily check-in count — darker cells = more activity.</CardDescription>
        </CardHeader>
        <CardContent>
          {heatmap.isLoading ? (
            <LoadingState />
          ) : heatmap.isError ? (
            <ErrorState title="Heatmap failed" description={humanizeError(heatmap.error)} />
          ) : (
            <Heatmap days={heatmap.data?.days ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent check-ins</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {checkins.isLoading ? (
            <LoadingState />
          ) : checkins.isError ? (
            <ErrorState title="Check-ins failed" description={humanizeError(checkins.error)} />
          ) : (checkins.data?.checkins?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No check-ins yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {checkins.data?.checkins.map((c) => (
                <li key={c.checkinId} className="flex items-center justify-between px-4 py-2">
                  <div>
                    <div className="font-medium">{c.localDate}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(c.checkinTimestampUTC).toUTCString()} · {c.playMode}
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    +{c.awardedPoints} XP
                    {c.teamPointsAwarded > 0 && ` · +${c.teamPointsAwarded} team`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Heatmap({ days }: { days: { date: string; count: number }[] }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(14px,1fr))] gap-1">
      {days.map((d) => {
        const intensity = d.count === 0 ? 0 : Math.min(4, Math.ceil((d.count / max) * 4));
        const bg =
          intensity === 0
            ? 'bg-muted'
            : intensity === 1
              ? 'bg-primary/20'
              : intensity === 2
                ? 'bg-primary/40'
                : intensity === 3
                  ? 'bg-primary/70'
                  : 'bg-primary';
        return (
          <div
            key={d.date}
            className={`h-3 w-3 rounded-sm ${bg}`}
            title={`${d.date}: ${d.count}`}
            aria-label={`${d.date}: ${d.count} check-ins`}
          />
        );
      })}
    </div>
  );
}

function PointsTab({ userId }: { userId: string }) {
  const q = useUserPointsHistory(userId, 30);
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Failed to load history" description={humanizeError(q.error)} />;
  const history = q.data?.history ?? [];
  if (history.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No XP events yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <ul className="divide-y text-sm">
          {history.map((e, i) => (
            <li key={`${e.occurredAt}-${i}`} className="flex items-center justify-between px-4 py-2">
              <div>
                <div className="font-medium">
                  {e.delta > 0 ? `+${e.delta}` : e.delta} · balance {e.newBalance}
                </div>
                <div className="text-xs text-muted-foreground">
                  {e.sourceType}
                  {e.sourceId ? ` · ${e.sourceId}` : ''} · {e.occurredAt}
                </div>
              </div>
              {e.teamId && <Badge variant="outline">team {shortId(e.teamId)}</Badge>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
